import React, { useEffect, useRef } from "react";

import { useTranslation } from "react-i18next";

import { Button, Label, TitleH2, TitleH4 } from "../generic";
import { FlexBox, StyledBlock } from "../containers";
import Modal from "../Modal";
import Calendar, { ICalendar } from "../Calendar";
import { MONTHS } from "../../utils/constants";
import { ButtonsDiv, IcoCalendar, IcoEdit, LabelEdit } from "./styles";
import { DateNone, IInputData } from "../../interfaces/intarfaces";
import {
  ContainerSelectedValue,
  ItemsMultiSelect,
} from "../MultiSelect/styles";
import { DateLine } from "pages/ProfileUser/SimpleComponents";
import { isoDate } from "utils/helpers";

const SelectDateRange: React.FC<
  ICalendar & {
    showEdit?: string;
    title: string;
    text: string;
    showDlg?: boolean;
    children: React.ReactNode;
  }
> = ({
  value,
  endInterval,
  showEdit = "",
  children,
  title,
  text,
  showDlg,
  ...props
}) => {
    const [showModal, setShowModal] = React.useState(0);
    const value_prev = useRef<DateNone>(undefined);
    const endInterval_prev = useRef<DateNone>(undefined);

    useEffect(() => {
      value_prev.current = value;
      endInterval_prev.current = endInterval;
      // eslint-disable-next-line
    }, []);

    useEffect(() => {
      if (showDlg !== undefined && showDlg) setShowModal(1);
    }, [showDlg]);

    const cancel_change = () => {
      props.onChange(value_prev.current, endInterval_prev.current);
      setShowModal(0);
    };

    return (
      <FlexBox justify="space-between">
        <FlexBox justify="flex-start" onClick={() => setShowModal(1)}>
          <IcoCalendar size="1.1em" fontColor={endInterval === undefined} />
          <DateLine
            start={isoDate(value)}
            end={isoDate(endInterval)}
            style={{ marginLeft: "0.5em", cursor: "pointer" }}
          />
        </FlexBox>
        {showEdit.length > 0 && (
          <FlexBox justify="flex-end" onClick={() => setShowModal(1)}>
            <LabelEdit colorEdit>{showEdit}</LabelEdit>
            <IcoEdit size="1.2em" />
          </FlexBox>
        )}

        {showModal > 0 && (
          <Modal isShow={!!showModal} handleClose={cancel_change}>
            <FlexBox
              justify="flex-end"
              direction="column"
              margin="1em"
              style={{ overflow: "auto", justifyItems: "flex-end" }}
            >
              <TitleH2 style={{ alignSelf: "flex-start" }}>{title}</TitleH2>
              <TitleH4>{text}</TitleH4>
              <hr />
              <Calendar value={value} endInterval={endInterval} {...props} />
              <ButtonsDiv>
                <Button normal onClick={cancel_change}>
                  Cancel
                </Button>
                {children}
              </ButtonsDiv>
            </FlexBox>
          </Modal>
        )}
      </FlexBox>
    );
  };

interface IDateRange extends IInputData, ICalendar {
  w?: string;
  grow?: string;
  id?: string;
  disabled?: boolean;
  required?: boolean;
  isClose?: boolean;
  anyEndDate?: boolean;
  isAnyDateOut?: boolean;
  isAnyDateIn?: boolean;
  modeView?: string;
  isIcon?: boolean;
  isDateInFirst?: number;
  leftBorder?: any;
  rightBorder?: any;
  rentalError?: string;
  handleLeave?: () => void;
  setIsAnyDateOut?: (isAnyDate: boolean) => void
  setIsDateInFirst?: (isDateIn: number) => void
}

export const DateRange: React.FC<IDateRange> = ({
  title = "",
  placeholder = "",
  isIcon = true,
  rentalError,
  prefix = undefined,
  children,
  value,
  endInterval,
  w = "100%",
  grow = "0",
  handleLeave,
  isClose,
  id,
  required = false,
  disabled,
  anyEndDate = false,
  setIsAnyDateOut,
  isAnyDateOut,
  leftBorder,
  rightBorder,
  setIsDateInFirst,
  isAnyDateIn,
  modeView,
  isDateInFirst,
  ...props
}) => {
  const { t } = useTranslation();

  const [showModal, setShowModal] = React.useState(0);

  React.useEffect(() => {
    if (isClose === undefined) return;
    if ("range" in props && props.range === true) {
      if (endInterval !== undefined) setShowModal(0);
    } else if (value !== undefined) setShowModal(0);
    // eslint-disable-next-line
  }, [value, endInterval]);

  const createLine = (): string => {
    if (value === undefined) return placeholder || t(`calendar.chooseDate`);
    if (!props.range)
      return `${value.getDate()} ${t(
        `calendar.months.${MONTHS[value.getMonth()]}`
      )
        .toLocaleLowerCase()
        .substr(0, 3)} ${value.getFullYear()}`;
    if (endInterval === undefined) return placeholder || t(`calendar.chooseDate`);
    return `${value.getDate()} ${t(
      `calendar.months.${MONTHS[value.getMonth()]}`
    )
      .toLocaleLowerCase()
      .substr(0, 3)} ${value.getFullYear() !== endInterval.getFullYear()
        ? value.getFullYear()
        : ""
      } - ${endInterval.getDate()} ${t(
        `calendar.months.${MONTHS[endInterval.getMonth()]}`
      )
        .toLocaleLowerCase()
        .substr(0, 3)} ${endInterval.getFullYear()}
      `;
  };

  return (
    <StyledBlock w={w} grow={grow} id={id}>
      {title && (
        <Label bgColor="transparent" p="0" fontWeight="600">
          {title}
        </Label>
      )}
      <ContainerSelectedValue
        onClick={() => {
          (setIsDateInFirst && !isAnyDateIn) && setIsDateInFirst(1)
          !disabled && setShowModal(1)
        }}
        //@ts-ignore
        error={((modeView === 're-publish' && !value) || (rentalError && rentalError.includes(value?.toISOString().slice(0, 10)) && !isAnyDateOut) || (required && value === undefined))}
      >
        {isIcon && <IcoCalendar
          size="1.1em"
          fontColor={
            props.range ? endInterval === undefined : value === undefined
          }
        />}
        <ItemsMultiSelect
          bgColor="transparent"
          muted={props.range ? endInterval === undefined : value === undefined}
        >
          {isAnyDateOut ? t("calendar.anyDate") : createLine()}
        </ItemsMultiSelect>
      </ContainerSelectedValue>
      {showModal > 0 && (
        <Modal
          isShow={!!showModal}
          handleClose={() => {
            handleLeave && handleLeave()
            setShowModal(0)
          }}
          children={
            <FlexBox
              justify="center"
              direction="column"
              margin="0.5em"
              style={{ overflow: "auto" }}
            >
              <Label fontSize="1.5em">{title}</Label>
              <Calendar value={value} anyEndDate={anyEndDate} rightBorder={rightBorder} leftBorder={leftBorder} endInterval={endInterval} {...props} setIsAnyDateOut={setIsAnyDateOut} />
              {anyEndDate && <Button main m='6px 0 0 0 ' onClick={() => {
                setShowModal(0)
                props.onChange(new Date('2101-01-01T00:00:00'), new Date('2101-01-01T00:00:00'))
                setIsAnyDateOut!(true)
              }}>
                {t("calendar.anyDate")}
              </Button>}
            </FlexBox>
          }
        />
      )}
    </StyledBlock>
  );
};

export default SelectDateRange;
